import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import twoFactorService from '../../services/twofactor.service';
import { TwoFactorForm } from '../../components/auth/TwoFactorForm';
import { useAuthStore } from '../../stores/auth.store';

/**
 * Portal login with mandatory two-factor authentication.
 *
 * Flow (see docs/SECURITY-2FA.md):
 *   1. email + password  -> server emails a one-time code
 *   2. one-time code     -> session cookie is issued
 *
 * A correct password alone never produces a session.
 */
type Step = 'password' | 'twofactor';

export function LoginPage() {
  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [challenge, setChallenge] = useState<{
    maskedEmail?: string;
    expiresInMinutes?: number;
    canRememberDevice?: boolean;
    trustedDeviceLifetimeDays?: number;
  }>({});
  const [isResuming, setIsResuming] = useState(true);
  const { initialize } = useAuthStore();

  // A refresh mid-challenge should resume at the code step, not silently drop it.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const status = await twoFactorService.getStatus();
      if (cancelled) return;
      if (status.pending) {
        setChallenge({
          maskedEmail: status.maskedEmail,
          canRememberDevice: status.canRememberDevice,
          trustedDeviceLifetimeDays: status.trustedDeviceLifetimeDays,
        });
        setStep('twofactor');
      }
      setIsResuming(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const result = await twoFactorService.login(email, password);

    if (result.success && !result.requiresTwoFactor) {
      // Lynki.Auth already trusts this device: the session is established, no code was sent.
      await initialize();
      // Full reload so every store re-reads the now authenticated session.
      window.location.href = '/';
      return;
    }

    if (result.success) {
      setChallenge({
        maskedEmail: result.maskedEmail,
        expiresInMinutes: result.expiresInMinutes,
        canRememberDevice: result.canRememberDevice,
        trustedDeviceLifetimeDays: result.trustedDeviceLifetimeDays,
      });
      setStep('twofactor');
    } else {
      setError(result.message);
    }
    setIsSubmitting(false);
  };

  const handleVerify = async (code: string, rememberDevice: boolean): Promise<string | null> => {
    const result = await twoFactorService.verifyTwoFactor(code, false, rememberDevice);

    if (result.success) {
      await initialize();
      // Full reload so every store re-reads the now authenticated session.
      window.location.href = '/';
      return null;
    }

    if (result.restart) {
      // The partial sign-in expired: send the user back to the password step.
      setStep('password');
      setPassword('');
      setError(result.message);
      return null;
    }

    return result.message;
  };

  const handleResend = async (): Promise<string> => {
    const result = await twoFactorService.resendCode();
    if (result.restart) {
      setStep('password');
      setPassword('');
      setError(result.message);
    }
    return result.message;
  };

  const handleBack = () => {
    setStep('password');
    setPassword('');
    setError(null);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Lynki</h1>
          <p className="mt-1 text-sm text-slate-500">Logg inn på portalen</p>
        </div>

        {isResuming ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto" />
        ) : step === 'password' ? (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                E-post
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Passord
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
            </div>

            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? 'Logger inn...' : 'Fortsett'}
            </button>

            <p className="text-xs text-slate-400 text-center">
              Innlogging krever en engangskode som sendes til e-posten din.
            </p>
          </form>
        ) : (
          <TwoFactorForm
            maskedEmail={challenge.maskedEmail}
            expiresInMinutes={challenge.expiresInMinutes}
            canRememberDevice={challenge.canRememberDevice}
            rememberDeviceDays={challenge.trustedDeviceLifetimeDays}
            onVerify={handleVerify}
            onResend={handleResend}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
}

export default LoginPage;
